/**
 * OPS 3.3 proposal composer — /proposals/new.
 *
 * Flow: pick client → pick type (RECURRING from the client's compliance
 * items / ONE_OFF from engagement sub-items) → tick source items and
 * hand-price each → set the ONE billing schedule (recurring) → tick clauses
 * from the library (editable copies, reorderable) → details → preview the
 * branded PDF → create. The proposal is a priced snapshot from this moment.
 *
 * OPS 3.5 B #3: the form itself lives in components/ProposalComposerForm.tsx
 * and is shared verbatim with the proposal edit modal — this page is only
 * the header + the create mutation.
 */

import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { ProposalComposerForm, apiError, type ComposerPayload } from '../components/ProposalComposerForm.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { ForbiddenPage } from '../components/ForbiddenPage.js';

export function ProposalComposerPage() {
  const navigate = useNavigate();

  const create = useMutation({
    mutationFn: async (payload: ComposerPayload) => {
      const res = await fetch('/v1/proposals/compose', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await apiError(res);
      const body = await res.json() as { ok: true; data: { id: string } };
      return body.data;
    },
    onSuccess: (p) => navigate(`/proposals/${p.id}`),
  });

  const { canDo } = useCapabilities();
  if (!canDo('proposals.create') || !canDo('billing.view')) return <ForbiddenPage />;

  return (
    <div className="w-full min-w-0 [&_textarea]:max-w-prose">
      <header className="mb-5">
        <button onClick={() => navigate(-1)} className="text-xs text-muted hover:text-white mb-2">← Back</button>
        <h1 className="text-xl font-semibold text-white">New proposal</h1>
        <p className="text-sm text-muted">Compose a priced snapshot — later edits to the source records never change it.</p>
      </header>
      <ProposalComposerForm
        mode="create"
        onSubmit={(payload) => create.mutate(payload)}
        submitting={create.isPending}
        submitLabel="Create proposal"
        error={create.error ? create.error.message : null}
      />
    </div>
  );
}
